import { supabase } from './supabaseClient';
import type { Subscription, UserPlan } from '@/types/database';
import { Capacitor } from '@capacitor/core';

export type CancelSubscriptionResult = {
  ok: boolean;
  provider: 'manual' | 'apple' | 'google';
  code?: string;
  message?: string;
  current_period_end?: string | null;
};

export type MobileStorePlatform = 'ios' | 'android';
export type MobileStoreStartResult = {
  platform: MobileStorePlatform;
  mode: 'native-plugin' | 'store-link';
  targetUrl?: string;
  /** Il dialog store è chiuso, ma la verifica backend è ancora in corso. */
  awaitsVerification?: boolean;
};

type DynamicPurchasePlugin = {
  subscribe?: (payload?: Record<string, unknown>) => Promise<unknown>;
  purchase?: (payload?: Record<string, unknown>) => Promise<unknown>;
  buy?: (payload?: Record<string, unknown>) => Promise<unknown>;
};

const IOS_PRODUCT_ID = String(import.meta.env.VITE_IAP_IOS_PRO_MONTHLY_PRODUCT_ID ?? '').trim();
// Product id dell'abbonamento Pro mensile su Google Play Billing: deve esistere
// in Play Console → Monetizzazione → Prodotti → Abbonamenti.
const ANDROID_PRODUCT_ID = String(import.meta.env.VITE_IAP_ANDROID_PRO_MONTHLY_PRODUCT_ID ?? 'swapwish_monthly_sub').trim();
const IOS_APP_STORE_APP_ID = String(import.meta.env.VITE_IOS_APP_STORE_APP_ID ?? '').trim();
const ANDROID_PACKAGE_ID = String(
  import.meta.env.VITE_ANDROID_APP_PACKAGE_ID ?? 'com.swapwish.app'
).trim();

function getMobileStorePlatform(): MobileStorePlatform | null {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') return platform;
  return null;
}

function resolveNativePurchasePlugin(): DynamicPurchasePlugin | null {
  const plugins = (Capacitor as any)?.Plugins ?? {};
  return (
    (plugins.Purchases as DynamicPurchasePlugin | undefined)
    ?? (plugins.InAppPurchase as DynamicPurchasePlugin | undefined)
    ?? (plugins.InAppPurchase2 as DynamicPurchasePlugin | undefined)
    ?? null
  );
}

function buildStoreUrl(platform: MobileStorePlatform): string {
  if (platform === 'ios') {
    if (IOS_APP_STORE_APP_ID) {
      return `https://apps.apple.com/app/id${encodeURIComponent(IOS_APP_STORE_APP_ID)}`;
    }
    return 'https://apps.apple.com/account/subscriptions';
  }

  if (ANDROID_PRODUCT_ID) {
    const sku = encodeURIComponent(ANDROID_PRODUCT_ID);
    const pkg = encodeURIComponent(ANDROID_PACKAGE_ID || 'com.swapwish.app');
    return `https://play.google.com/store/account/subscriptions?sku=${sku}&package=${pkg}`;
  }
  return 'https://play.google.com/store/account/subscriptions';
}

function openExternalUrl(url: string) {
  if (typeof window === 'undefined') {
    throw new Error('Browser environment is not available');
  }

  try {
    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) {
      window.location.assign(url);
    }
    return;
  } catch {
    window.location.assign(url);
  }
}

// ── Google Play Billing via cordova-plugin-purchase (CdvPurchase) ──────────
// Flusso: initialize (una volta) → order(offer) → evento approved →
// invoke('play-billing', { action: 'verify', purchaseToken }) → finish().
// L'applicationUsername (= auth user id) diventa obfuscatedExternalAccountId
// su Google Play, così il webhook RTDN può ricollegare l'acquisto all'utente.

// deno-lint non applicabile qui: il plugin espone un global non tipizzato.
function getCdvPurchase(): any | null {
  return (window as any)?.CdvPurchase ?? null;
}

function purchaseTokenFromTransaction(transaction: any): string {
  return String(
    transaction?.nativePurchase?.purchaseToken
    ?? transaction?.purchaseId
    ?? transaction?.parentReceipt?.purchaseToken
    ?? ''
  ).trim();
}

async function purchaseVerificationErrorCode(error: unknown): Promise<string> {
  const response = (error as any)?.context;
  if (!response || typeof response.json !== 'function') return '';
  const payload = await response.json().catch(() => null);
  return String(payload?.code ?? '').trim();
}

function notifyPurchaseVerificationFailure(code: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('swapwish:subscription-verification-failed', {
    detail: { code },
  }));
}

let cdvInitPromise: Promise<boolean> | null = null;

async function initCdvPurchaseStore(userId: string): Promise<boolean> {
  const cdv = getCdvPurchase();
  if (!cdv?.store || !ANDROID_PRODUCT_ID) return false;
  if (cdvInitPromise) return cdvInitPromise;
  cdvInitPromise = (async () => {
    const { store, ProductType, Platform, LogLevel } = cdv;
    store.verbosity = LogLevel?.WARNING ?? 1;
    store.applicationUsername = () => userId;
    store.register([{
      id: ANDROID_PRODUCT_ID,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.GOOGLE_PLAY,
    }]);
    store.when().approved(async (transaction: any) => {
      let verified = false;
      try {
        const purchaseToken = purchaseTokenFromTransaction(transaction);
        if (!purchaseToken) {
          console.error('[iap] purchase token missing from approved transaction', transaction);
          notifyPurchaseVerificationFailure('google_purchase_token_missing');
          return;
        }
        if (purchaseToken) {
          // Registra/verifica lato server: upsert in subscriptions + acknowledge.
          const { data, error } = await supabase.functions.invoke('play-billing', {
            body: { action: 'verify', purchaseToken },
          });
          if (error) {
            const code = await purchaseVerificationErrorCode(error);
            console.error('[iap] play-billing verify failed', { code, error });
            notifyPurchaseVerificationFailure(code);
            return;
          } else if (String((data as any)?.status ?? '') !== 'active') {
            console.warn('[iap] play-billing verify returned non-active status', data);
            notifyPurchaseVerificationFailure('google_purchase_not_active');
            return;
          } else {
            verified = true;
            if (typeof window !== 'undefined') {
            // Verify riuscita: il piano è cambiato lato server ma lo store client
            // non lo sa (nessun refresh dopo l'acquisto → l'utente restava "Free"
            // finché non riavviava l'app). Si notifica lo store, che ricarica
            // piano e profilo così isPro si aggiorna subito.
            window.dispatchEvent(new Event('swapwish:subscription-updated'));
            }
          }
        }
      } catch (e) {
        console.error('[iap] verify error', e);
        notifyPurchaseVerificationFailure('google_purchase_verification_failed');
        return;
      }
      if (!verified) return;
      try { transaction.finish(); } catch { /* già conclusa */ }
    });
    await store.initialize([Platform.GOOGLE_PLAY]);
    return true;
  })().catch((e) => {
    console.error('[iap] store initialize failed', e);
    cdvInitPromise = null;
    return false;
  });
  return cdvInitPromise;
}

async function tryCdvPurchaseSubscription(): Promise<boolean> {
  const userId = (await supabase.auth.getSession()).data.session?.user?.id;
  if (!userId) return false;
  const ready = await initCdvPurchaseStore(userId);
  if (!ready) return false;
  const cdv = getCdvPurchase();
  const product = cdv?.store?.get?.(ANDROID_PRODUCT_ID, cdv.Platform.GOOGLE_PLAY);
  const offer = product?.getOffer?.();
  if (!offer) {
    console.warn('[iap] product/offer non disponibile per', ANDROID_PRODUCT_ID);
    return false;
  }
  const orderError = await cdv.store.order(offer);
  if (orderError?.isError) {
    const error = new Error(String(orderError.message ?? 'PLAY_BILLING_ORDER_FAILED'));
    (error as any).code = orderError.code;
    throw error;
  }
  return true;
}

async function tryNativePluginPurchase(platform: MobileStorePlatform): Promise<boolean> {
  const plugin = resolveNativePurchasePlugin();
  if (!plugin) return false;

  const productId = platform === 'ios' ? IOS_PRODUCT_ID : ANDROID_PRODUCT_ID;
  if (!productId) return false;

  const payload = {
    productId,
    sku: productId,
    type: 'subs',
  };

  if (typeof plugin.subscribe === 'function') {
    await plugin.subscribe(payload);
    return true;
  }
  if (typeof plugin.purchase === 'function') {
    await plugin.purchase(payload);
    return true;
  }
  if (typeof plugin.buy === 'function') {
    await plugin.buy(payload);
    return true;
  }
  return false;
}

export const subscriptionService = {
  /** Piano effettivo dell'utente. Passa userId per evitare una chiamata extra a getSession(). */
  async myPlan(userId?: string): Promise<UserPlan | null> {
    const me = userId ?? (await supabase.auth.getSession()).data.session?.user?.id;
    if (!me) return null;
    const { data, error } = await supabase
      .from('v_user_plan')
      .select('*')
      .eq('user_id', me)
      .maybeSingle();
    if (error) throw error;
    return data as UserPlan | null;
  },

  async mySubscription(userId?: string): Promise<Subscription | null> {
    const me = userId ?? (await supabase.auth.getSession()).data.session?.user?.id;
    if (!me) return null;
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', me)
      .in('status', ['active', 'trialing', 'past_due', 'canceled'])
      .order('current_period_end', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return (data as Subscription) ?? null;
  },

  getMobileStorePlatform,

  /** Avvia acquisto Pro mensile su store mobile (best effort). */
  async startMobileStoreSubscription(): Promise<MobileStoreStartResult> {
    const platform = getMobileStorePlatform();
    if (!platform) {
      throw new Error('MOBILE_STORE_ONLY');
    }

    if (platform === 'android') {
      const usedPlayBilling = await tryCdvPurchaseSubscription();
      if (usedPlayBilling) {
        return { platform, mode: 'native-plugin', awaitsVerification: true };
      }

      // Il link esterno del Play Store non restituisce il purchase token alla
      // WebView: l'app non potrebbe verificarlo né fare acknowledge e Google
      // rimborserà automaticamente l'ordine. Su Android accettiamo quindi
      // solo il flusso Billing nativo, che consegna il token al backend.
      throw new Error('PLAY_BILLING_UNAVAILABLE');
    }

    const usedNativePlugin = await tryNativePluginPurchase(platform).catch(() => false);
    if (usedNativePlugin) {
      return { platform, mode: 'native-plugin' };
    }

    const targetUrl = buildStoreUrl(platform);
    openExternalUrl(targetUrl);
    return { platform, mode: 'store-link', targetUrl };
  },

  /** Richiede annullamento abbonamento (provider-ruled; può ritornare manual_required). */
  async cancelMySubscription(): Promise<CancelSubscriptionResult> {
    const { data, error } = await supabase.functions.invoke('cancel-subscription', { body: {} });
    if (error) {
      const response = (error as any)?.context;
      if (response && typeof response.json === 'function') {
        const payload = await response.json().catch(() => null);
        if (payload) {
          const err = new Error(payload?.error ?? error.message ?? 'Subscription cancel failed');
          (err as any).code = payload?.code;
          throw err;
        }
      }
      throw error;
    }
    return (data as CancelSubscriptionResult) ?? { ok: false, provider: 'manual' };
  },
};
